import type {
  TiptapNode,
  TiptapMark,
  ElementalNode,
  ElementalContent,
  ElementalTextContentNode,
  TiptapDoc,
} from "../../../types";
import { v4 as uuidv4 } from "uuid";
import { isValidVariableName } from "@/components/utils/validateVariableName";
import { isBlankImageSrc } from "../image";
import { defaultButtonProps } from "@/components/extensions/Button/Button";
import {
  inboxStyleFromElementalStyle,
  isLegacyOutlinedBackground,
} from "@/components/extensions/Button/inboxButtonStyle";
import { lineHeightToPx, parsePxValue } from "../cssValues";
import {
  getTierFontSizePx,
  isPresetSizedTier,
  type TextTier,
} from "@/lib/constants/email-editor-tiptap-styles";

const textStyleToHeadingLevel: Record<string, number> = { h1: 1, h2: 2, h3: 3, subtext: 3 };

/**
 * Read the block-level typography overrides off an Elemental node into TipTap
 * attributes, both as px numbers.
 *
 * A unitless `line_height` is a multiplier, so it only means anything against a
 * font size — and it has to be the *effective* one, resolved the way the renderer
 * does: the block's own `font_size`, else the document base (which the renderer
 * applies to the body tiers but not to headings or subtext), else the tier preset.
 * Resolving against the preset while a document base was in force would both
 * mis-render the canvas and, on the next save, rewrite the stored `line_height`
 * to the wrong px value.
 *
 * Values the renderer would reject are dropped rather than carried through.
 */
const readTypographyAttrs = (
  node: { font_size?: string; line_height?: string },
  tier: TextTier,
  isQuote = false,
  documentFontSize?: number
): { fontSize?: number; lineHeight?: number } => {
  const attrs: { fontSize?: number; lineHeight?: number } = {};

  const fontSize = parsePxValue(node.font_size);
  if (fontSize !== undefined) {
    attrs.fontSize = fontSize;
  }

  const inheritedFontSize = isPresetSizedTier(tier) ? undefined : documentFontSize;
  const effectiveFontSize = fontSize ?? inheritedFontSize ?? getTierFontSizePx(tier, isQuote);

  const lineHeight = lineHeightToPx(node.line_height, effectiveFontSize);
  if (lineHeight !== undefined) {
    attrs.lineHeight = lineHeight;
  }

  return attrs;
};

/** Convert Elemental's "full" alignment to TipTap's "justify". */
const elementalAlignToTiptap = (align: string | undefined): string => {
  if (align === "full") return "justify";
  return align || "left";
};

/**
 * Coerce a deprecated quote frame value to a number. Stored quotes carry these
 * as numbers in most cases but as strings in some, so accept both.
 */
const parseQuoteFrameValue = (value: number | string | undefined): number | undefined => {
  if (typeof value === "number") return Number.isFinite(value) ? value : undefined;
  if (typeof value !== "string") return undefined;
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

/**
 * Read a quote's frame (left border width + padding) into Blockquote attrs.
 *
 * `border_left_width`, `padding_horizontal` and `padding_vertical` are
 * designer-only fields that no render path reads, and that the designer exposes
 * no control for. The serializer stopped emitting them in C-19960, but stored
 * content still carries them — so they are read here to keep those quotes
 * looking the same in the editor, and are dropped on the next save.
 */
const readQuoteFrameAttrs = (node: {
  border_left_width?: number | string;
  padding_horizontal?: number | string;
  padding_vertical?: number | string;
}): { borderLeftWidth?: number; paddingVertical?: number; paddingHorizontal?: number } => {
  const attrs: { borderLeftWidth?: number; paddingVertical?: number; paddingHorizontal?: number } =
    {};

  const borderLeftWidth = parseQuoteFrameValue(node.border_left_width);
  if (borderLeftWidth !== undefined) {
    attrs.borderLeftWidth = borderLeftWidth;
  }

  const paddingVertical = parseQuoteFrameValue(node.padding_vertical);
  if (paddingVertical !== undefined) {
    attrs.paddingVertical = paddingVertical;
  }

  const paddingHorizontal = parseQuoteFrameValue(node.padding_horizontal);
  if (paddingHorizontal !== undefined) {
    attrs.paddingHorizontal = paddingHorizontal;
  }

  return attrs;
};

/**
 * Convert an Elemental elements array (type: "string" | "link" sub-elements with boolean
 * formatting flags) into TipTap nodes. Handles variables ({{var}}) and newlines (\n → hardBreak).
 */
export function convertElementsArrayToTiptapNodes(
  elements: ElementalTextContentNode[]
): TiptapNode[] {
  const nodes: TiptapNode[] = [];

  for (const el of elements) {
    if (el.type === "string") {
      convertStringElementToTiptapNodes(el, nodes);
    } else if (el.type === "link") {
      convertLinkElementToTiptapNodes(el, nodes);
    }
    // "img" type can be added later if needed
  }

  return nodes;
}

/** Build TipTap marks array from Elemental boolean formatting flags. */
function buildMarksFromFlags(el: ElementalTextContentNode): TiptapMark[] {
  const marks: TiptapMark[] = [];
  if (el.bold) marks.push({ type: "bold" });
  if (el.italic) marks.push({ type: "italic" });
  if (el.strikethrough) marks.push({ type: "strike" });
  if (el.underline) marks.push({ type: "underline" });

  // color and font_size share the single textStyle mark
  const fontSize = parsePxValue(el.font_size);
  if (el.color || fontSize !== undefined) {
    marks.push({
      type: "textStyle",
      attrs: {
        ...(el.color && { color: el.color }),
        ...(fontSize !== undefined && { fontSize: `${fontSize}px` }),
      },
    });
  }
  return marks;
}

/** Convert an ElementalStringTextContent to TipTap nodes, handling \n and {{variables}}. */
function convertStringElementToTiptapNodes(
  el: ElementalTextContentNode,
  nodes: TiptapNode[]
): void {
  const content = "content" in el ? el.content : "";
  if (!content) return;

  const marks = buildMarksFromFlags(el);

  // Split by \n to produce hardBreaks
  const lines = content.split("\n");
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line) {
      // Parse variables within the line text
      parseTextSegmentWithVariables(line, marks, nodes);
    }

    // Add hardBreak between lines (not after the last one)
    if (i < lines.length - 1) {
      nodes.push({ type: "hardBreak" });
    }
  }
}

/** Convert an ElementalLinkTextContent to TipTap nodes with a link mark. */
function convertLinkElementToTiptapNodes(el: ElementalTextContentNode, nodes: TiptapNode[]): void {
  if (el.type !== "link") return;
  const content = el.content || "";
  if (!content) return;

  const marks = buildMarksFromFlags(el);
  const linkAttrs: Record<string, unknown> = { href: el.href };
  if (el.disable_tracking) {
    linkAttrs.disableTracking = true;
  }
  marks.push({ type: "link", attrs: linkAttrs });

  // Parse variables within link text
  parseTextSegmentWithVariables(content, marks, nodes);
}

/**
 * Parse a text segment, extracting {{variable}} patterns and creating
 * TipTap text/variable nodes with the given marks.
 */
function parseTextSegmentWithVariables(
  text: string,
  marks: TiptapMark[],
  nodes: TiptapNode[]
): void {
  if (!text) return;

  const variableRegex = /\{\{([^}]*)\}\}/g;
  let match;
  let lastIndex = 0;
  variableRegex.lastIndex = 0;

  while ((match = variableRegex.exec(text)) !== null) {
    // Add text before the variable
    if (match.index > lastIndex) {
      const beforeText = text.substring(lastIndex, match.index);
      if (beforeText) {
        nodes.push({
          type: "text",
          text: beforeText,
          ...(marks.length > 0 && { marks: [...marks] }),
        });
      }
    }

    // Add variable node
    const variableName = match[1].trim();
    const isValid = variableName === "" || isValidVariableName(variableName);
    const hasLinkMark = marks.some((m) => m.type === "link");
    nodes.push({
      type: "variable",
      attrs: {
        id: variableName,
        isInvalid: !isValid,
        ...(hasLinkMark && { inUrlContext: true }),
      },
      ...(marks.length > 0 && { marks: [...marks] }),
    });

    lastIndex = match.index + match[0].length;
  }

  // Add any remaining text after the last variable
  if (lastIndex < text.length) {
    const remainingText = text.substring(lastIndex);
    if (remainingText) {
      nodes.push({
        type: "text",
        text: remainingText,
        ...(marks.length > 0 && { marks: [...marks] }),
      });
    }
  }
}

export function parseMDContent(content: string): TiptapNode[] {
  const nodes: TiptapNode[] = [];
  const textNodes = content.replace(/\n$/, "").split("\n");

  for (let i = 0; i < textNodes.length; i++) {
    parseTextLine(textNodes[i], nodes);

    // Add hardBreak if not the last node
    if (i < textNodes.length - 1) {
      nodes.push({ type: "hardBreak" });
    }
  }

  return nodes;
}

// Helper function to parse text line with variables
function parseTextLine(line: string, nodes: TiptapNode[]): void {
  // Process markdown formatting
  processMarkdownFormatting(line, nodes);
}

// Process markdown formatting using complete pattern matching
// This approach matches complete markdown patterns (e.g., *text*) rather than individual markers
function processMarkdownFormatting(text: string, nodes: TiptapNode[]): void {
  if (!text) return;

  // Use a single regex to match all formatted patterns and variables
  // Order matters: longer patterns first (** before *, __ before _)
  // Patterns require at least one non-marker character between delimiters
  const patterns = [
    // Variables: {{variable}} or {}variable{}
    { regex: /\{\{([^}]*)\}\}/g, type: "variable" },
    { regex: /\{\}([^{}]+)\{\}/g, type: "variable" },
    // Links: [text](url)
    { regex: /\[([^\]]+)\]\(([^)]+)\)/g, type: "link" },
    // Bold: **text** or __text__
    { regex: /\*\*([^*]+)\*\*/g, type: "bold" },
    { regex: /__([^_]+)__/g, type: "bold" },
    // Italic: *text* or _text_ (but not inside ** or __)
    { regex: /(?<!\*)\*([^*]+)\*(?!\*)/g, type: "italic" },
    { regex: /(?<!_)_([^_]+)_(?!_)/g, type: "italic" },
    // Strikethrough: ~text~
    { regex: /~([^~]+)~/g, type: "strike" },
    // Underline: +text+
    { regex: /\+([^+]+)\+/g, type: "underline" },
  ];

  interface TextSegment {
    start: number;
    end: number;
    text: string;
    type: "plain" | "bold" | "italic" | "strike" | "underline" | "link" | "variable";
    marks?: string[];
    attrs?: { href?: string; id?: string; isInvalid?: boolean };
  }

  // Find all matches from all patterns
  const allMatches: TextSegment[] = [];

  for (const pattern of patterns) {
    const regex = new RegExp(pattern.regex.source, pattern.regex.flags);
    let match;
    while ((match = regex.exec(text)) !== null) {
      if (pattern.type === "variable") {
        const variableName = match[1].trim();
        // For empty variables (newly inserted, being edited), don't mark as invalid
        // Validation will happen on blur in VariableChipBase
        // Only validate non-empty variable names
        const isValid = variableName === "" || isValidVariableName(variableName);
        allMatches.push({
          start: match.index,
          end: match.index + match[0].length,
          text: match[1],
          type: "variable",
          attrs: { id: variableName, isInvalid: !isValid },
        });
      } else if (pattern.type === "link") {
        allMatches.push({
          start: match.index,
          end: match.index + match[0].length,
          text: match[1],
          type: "link",
          attrs: { href: match[2] },
        });
      } else {
        allMatches.push({
          start: match.index,
          end: match.index + match[0].length,
          text: match[1],
          type: pattern.type as TextSegment["type"],
        });
      }
    }
  }

  // Sort matches by start position
  allMatches.sort((a, b) => a.start - b.start);

  // Remove overlapping matches (keep the first/longest one)
  const nonOverlappingMatches: TextSegment[] = [];
  let lastEnd = 0;
  for (const match of allMatches) {
    if (match.start >= lastEnd) {
      nonOverlappingMatches.push(match);
      lastEnd = match.end;
    }
  }

  // Build the final nodes array
  const finalNodes: TiptapNode[] = [];
  let currentPos = 0;

  for (const match of nonOverlappingMatches) {
    // Add plain text before this match
    if (match.start > currentPos) {
      const plainText = text.substring(currentPos, match.start);
      if (plainText) {
        parseTextWithVariables(plainText, [], finalNodes);
      }
    }

    // Add the matched content with appropriate formatting
    if (match.type === "variable") {
      finalNodes.push({
        type: "variable",
        attrs: { id: match.attrs?.id, isInvalid: match.attrs?.isInvalid },
      });
    } else if (match.type === "link") {
      const linkNodes: TiptapNode[] = [];
      // Recursively process the link text for nested formatting
      processMarkdownFormatting(match.text, linkNodes);
      linkNodes.forEach((node) => {
        node.marks = node.marks || [];
        node.marks.push({
          type: "link",
          attrs: { href: match.attrs?.href },
        });
      });
      finalNodes.push(...linkNodes);
    } else {
      // Bold, italic, strike, underline
      const formattedNodes: TiptapNode[] = [];
      // Recursively process for nested formatting
      processMarkdownFormatting(match.text, formattedNodes);
      // Apply the mark to all nodes
      formattedNodes.forEach((node) => {
        node.marks = node.marks || [];
        node.marks.push({ type: match.type });
      });
      finalNodes.push(...formattedNodes);
    }

    currentPos = match.end;
  }

  // Add any remaining plain text
  if (currentPos < text.length) {
    const remainingText = text.substring(currentPos);
    if (remainingText) {
      parseTextWithVariables(remainingText, [], finalNodes);
    }
  }

  // Replace original nodes array content
  // nodes.length = 0;
  nodes.push(...finalNodes);
}

// Helper function to parse text with variables and handle marks
function parseTextWithVariables(
  text: string,
  marks: { text: string; href?: string; type: string }[],
  nodes: TiptapNode[]
): void {
  if (!text) return; // Skip empty text

  // Use a more robust regex that ensures we match complete {{variable}} patterns
  // The pattern matches {{ followed by one or more non-} characters, then }}
  const variableRegex = /\{\{([^}]*)\}\}/g;
  let match;
  let lastIndex = 0;

  // Reset regex lastIndex to ensure clean matching
  variableRegex.lastIndex = 0;

  while ((match = variableRegex.exec(text)) !== null) {
    // Ensure we have a complete match with both opening and closing braces
    if (!match[0].startsWith("{{") || !match[0].endsWith("}}")) {
      // Skip incomplete matches
      continue;
    }

    // Add text before the variable if it exists
    if (match.index > lastIndex) {
      const beforeText = text.substring(lastIndex, match.index);
      if (beforeText) {
        nodes.push({
          type: "text",
          text: beforeText,
          ...(marks.length > 0 && { marks }),
        });
      }
    }

    // Extract variable name (match[1] contains the captured group)
    const variableName = match[1].trim();

    // Check if this variable is inside a link
    const hasLinkMark = marks.some((mark) => mark.type === "link");

    // For empty variables (newly inserted, being edited), don't mark as invalid
    // Validation will happen on blur in VariableChipBase
    // Only validate non-empty variable names
    const isValid = variableName === "" || isValidVariableName(variableName);
    nodes.push({
      type: "variable",
      attrs: {
        id: variableName,
        isInvalid: !isValid,
        ...(hasLinkMark && { inUrlContext: true }),
      },
      ...(marks.length > 0 && { marks }),
    });

    lastIndex = match.index + match[0].length;
  }

  // Add any remaining text after the last variable (or the entire text if no variables were found)
  if (lastIndex < text.length) {
    const remainingText = text.substring(lastIndex);
    if (remainingText) {
      nodes.push({
        type: "text",
        text: remainingText,
        ...(marks.length > 0 && { marks }),
      });
    }
  }
}

export interface ConvertElementalToTiptapOptions {
  channel?: string; // e.g., 'email', 'sms'
}

export function convertElementalToTiptap(
  elemental: ElementalContent | null | undefined, // Allow null or undefined
  options?: ConvertElementalToTiptapOptions
): TiptapDoc {
  const emptyTiptapDoc: TiptapDoc = { type: "doc", content: [] };

  if (!elemental || !elemental.elements || elemental.elements.length === 0) {
    return emptyTiptapDoc;
  }

  let targetChannelElements: ElementalNode[] | undefined = undefined;
  const specifiedChannelName = options?.channel;

  // Find the target channel
  let targetChannel: ElementalNode | undefined = undefined;

  for (const topLevelElement of elemental.elements) {
    if (topLevelElement.type === "channel") {
      const channelNode = topLevelElement as ElementalNode & {
        channel?: string;
        elements?: ElementalNode[];
        raw?: {
          text?: string;
          title?: string;
          subject?: string;
          [key: string]: unknown;
        };
      };

      if (specifiedChannelName) {
        if (channelNode.channel === specifiedChannelName) {
          targetChannel = channelNode;
          break; // Found the specified channel
        }
      } else {
        // No specific channel name, use the first one found
        targetChannel = channelNode;
        break;
      }
    }
  }

  if (!targetChannel || targetChannel.type !== "channel") {
    return emptyTiptapDoc; // No suitable channel found
  }

  const channelNode = targetChannel as ElementalNode & {
    channel?: string;
    elements?: ElementalNode[];
    font_size?: string;
    raw?: {
      text?: string;
      title?: string;
      subject?: string;
      [key: string]: unknown;
    };
  };

  // The document-level base font size, needed to resolve a unitless block-level
  // `line_height` against the size that block actually renders at.
  const documentFontSize = parsePxValue(channelNode.font_size);

  // Get elements from the channel (raw is passed through, not converted)
  if (channelNode.elements && channelNode.elements.length > 0) {
    targetChannelElements = channelNode.elements;

    // For Push: convert meta to H2 for editor display
    if (channelNode.channel === "push") {
      targetChannelElements = targetChannelElements.map((element) => {
        if (element.type === "meta" && "title" in element) {
          return {
            type: "text" as const,
            content: element.title || "\n",
            text_style: "h2" as const,
          };
        }
        return element;
      });
    }
  } else {
    // No elements - use default empty content
    // raw is ignored and passed through (like locales)
    targetChannelElements = [{ type: "text" as const, content: "\n" }];
  }

  if (!targetChannelElements || targetChannelElements.length === 0) {
    return emptyTiptapDoc; // No suitable channel content found
  }

  const convertNode = (node: ElementalNode): TiptapNode[] => {
    if ((node as ElementalNode & { visible?: boolean }).visible === false) {
      return [];
    }

    // Skip meta nodes as they are just for storing the subject
    if (node.type === "meta") {
      return [];
    }

    switch (node.type) {
      case "text":
        if ("content" in node) {
          const headingLevel = node.text_style
            ? (textStyleToHeadingLevel[node.text_style] ?? null)
            : null;

          // Parse padding values if present
          let paddingAttrs = {};
          if (node.padding) {
            const [verticalStr, horizontalStr] = node.padding.replace(/px/g, "").split(" ");
            const vertical = parseInt(verticalStr);
            const horizontal = parseInt(horizontalStr);
            if (!isNaN(vertical) && !isNaN(horizontal)) {
              paddingAttrs = {
                paddingVertical: vertical,
                paddingHorizontal: horizontal,
              };
            }
          }

          // Parse border values if present
          // Support both flat properties (border_color, border_size) and legacy nested format (border.color, border.size)
          let borderAttrs = {};
          if (node.border_size || node.border?.enabled || node.border?.size) {
            const borderSize = node.border_size || node.border?.size;
            const borderColor = node.border_color || node.border?.color;
            borderAttrs = {
              ...(borderSize && { borderWidth: parseInt(borderSize) }),
              ...(borderColor && { borderColor }),
            };
          }

          // Handle empty content - ensure it becomes an empty paragraph
          const contentNodes = node.content.trim() ? parseMDContent(node.content) : [];

          return [
            {
              type: headingLevel ? "heading" : "paragraph",
              attrs: {
                textAlign: elementalAlignToTiptap(node.align),
                level: headingLevel,
                id: `node-${uuidv4()}`,
                ...paddingAttrs,
                ...borderAttrs,
                ...readTypographyAttrs(
                  node,
                  (node.text_style ?? "text") as TextTier,
                  false,
                  documentFontSize
                ),
                ...(node.background_color && { backgroundColor: node.background_color }),
                ...(node.locales && { locales: node.locales }),
                ...(node.if !== undefined && { if: node.if }),
              },
              content: contentNodes,
            },
          ];
        } else if ("elements" in node) {
          // New elements array format — boolean formatting flags, no markdown parsing needed
          const headingLevel = node.text_style
            ? (textStyleToHeadingLevel[node.text_style] ?? null)
            : null;

          // Parse padding values if present
          let paddingAttrs = {};
          if (node.padding) {
            const [verticalStr, horizontalStr] = node.padding.replace(/px/g, "").split(" ");
            const vertical = parseInt(verticalStr);
            const horizontal = parseInt(horizontalStr);
            if (!isNaN(vertical) && !isNaN(horizontal)) {
              paddingAttrs = {
                paddingVertical: vertical,
                paddingHorizontal: horizontal,
              };
            }
          }

          // Parse border values if present
          let borderAttrs = {};
          if (node.border_size || node.border?.enabled || node.border?.size) {
            const borderSize = node.border_size || node.border?.size;
            const borderColor = node.border_color || node.border?.color;
            borderAttrs = {
              ...(borderSize && { borderWidth: parseInt(borderSize) }),
              ...(borderColor && { borderColor }),
            };
          }

          const contentNodes = convertElementsArrayToTiptapNodes(node.elements);

          return [
            {
              type: headingLevel ? "heading" : "paragraph",
              attrs: {
                textAlign: elementalAlignToTiptap(node.align),
                level: headingLevel,
                id: `node-${uuidv4()}`,
                ...paddingAttrs,
                ...borderAttrs,
                ...readTypographyAttrs(
                  node,
                  (node.text_style ?? "text") as TextTier,
                  false,
                  documentFontSize
                ),
                ...(node.background_color && { backgroundColor: node.background_color }),
                ...(node.locales && { locales: node.locales }),
                ...(node.if !== undefined && { if: node.if }),
              },
              content: contentNodes,
            },
          ];
        }
        return [];

      case "action": {
        const isInboxChannel = specifiedChannelName === "inbox";

        // The Inbox sidebar owns these buttons, so an action on that channel is resolved
        // against the Inbox presets: the style decides how `background_color` should be read,
        // and the preset supplies whatever the payload did not carry.
        const inboxStyle = isInboxChannel
          ? inboxStyleFromElementalStyle(node.style, node.background_color)
          : undefined;

        // A template saved under the old encoding carries white as a marker rather than a
        // color anyone picked. Letting it through would draw a white outline on a white
        // canvas, so the preset accent stands in and re-saving writes it back properly.
        const carriesLegacyMarker =
          inboxStyle === "secondary" && isLegacyOutlinedBackground(node.background_color);

        // No color or sizing defaults for an Inbox action. It carries only its style, and the
        // canvas derives the look from that (see `actionLookFromStyle`) exactly as the Inbox
        // does. Injecting defaults here would put them on the node, and the canvas converter
        // would then emit them back out as if the author had chosen them.

        const contentText = node.content || defaultButtonProps.label;

        // Parse content to extract variables and create proper TipTap nodes
        const contentNodes: TiptapNode[] = [];
        parseTextWithVariables(contentText, [], contentNodes);

        // If no nodes were created (empty content), create a default text node
        if (contentNodes.length === 0) {
          contentNodes.push({ type: "text", text: contentText || defaultButtonProps.label });
        }

        // Parse border - support both flat properties and legacy nested format
        const borderRadiusRaw = node.border_radius ?? node.border?.radius;
        const borderColor = node.border?.color; // Legacy only, for backward compat

        // Parse border_radius - can be "21px" string or legacy number
        let borderRadius: number | undefined;
        if (typeof borderRadiusRaw === "string") {
          borderRadius = parseInt(borderRadiusRaw);
        } else if (typeof borderRadiusRaw === "number") {
          borderRadius = borderRadiusRaw;
        }

        // An Inbox action is its own node type, sharing nothing with the email button — see
        // `InboxAction.types`. It stores a label, a link and a style, so there is no color or
        // sizing on it to be written back out as though an author had chosen it.
        if (inboxStyle) {
          return [
            {
              type: "inboxAction",
              content: contentNodes,
              attrs: {
                label: contentText,
                link: node.href,
                actionStyle: inboxStyle,
                align: node.align === "full" ? "left" : node.align || "left",
                id: `node-${uuidv4()}`,
                ...(node.disable_tracking && { disableTracking: true }),
                ...(node.locales && { locales: node.locales }),
                ...(node.if !== undefined && { if: node.if }),
              },
            },
          ];
        }

        return [
          {
            type: "button",
            content: contentNodes,
            attrs: {
              label: contentText,
              link: node.href,
              alignment: node.align === "full" ? "center" : node.align || "center",
              id: `node-${uuidv4()}`,
              // `actionStyle` marks an Inbox action and nothing else: it is what makes the
              // canvas take the kit's look and drop the node's colors. An action on another
              // channel keeps its style under the legacy `style` attribute, exactly as before,
              // so an email action that happens to carry `style: "link"` is still an email
              // button drawn with its author's colors.
              ...(inboxStyle
                ? { actionStyle: inboxStyle }
                : node.style
                  ? { style: node.style }
                  : {}),
              ...(node.disable_tracking && { disableTracking: true }),
              ...(node.background_color &&
                !carriesLegacyMarker && { backgroundColor: node.background_color }),
              ...(() => {
                const fontSize = parsePxValue(node.font_size);
                return fontSize !== undefined ? { fontSize } : {};
              })(),
              ...(node.color && !carriesLegacyMarker && { textColor: node.color }),
              ...(node.padding &&
                (() => {
                  const parts = node.padding.replace(/px/g, "").trim().split(/\s+/);
                  const vertical = parseInt(parts[0]) || 0;
                  const horizontal = parts.length > 1 ? parseInt(parts[1]) || 0 : vertical;
                  return {
                    paddingVertical: Math.max(0, vertical),
                    paddingHorizontal: Math.max(0, horizontal),
                  };
                })()),
              ...(borderRadius !== undefined && { borderRadius }),
              ...(borderColor && { borderColor }), // Legacy backward compat
              ...(node.locales && { locales: node.locales }),
              ...(node.if !== undefined && { if: node.if }),
            },
          },
        ];
      }

      case "quote": {
        // Helper to detect if content is a markdown-style list
        const isListContent = (
          content: string | undefined
        ): { isList: boolean; isOrdered: boolean } => {
          if (!content) return { isList: false, isOrdered: false };
          const lines = content.split("\n").filter((l) => l.trim());
          if (lines.length === 0) return { isList: false, isOrdered: false };

          // Check if all lines start with bullet (•) or numbered (1. 2. etc.)
          const bulletPattern = /^•\s+/;
          const numberedPattern = /^\d+\.\s+/;

          const allBullets = lines.every((line) => bulletPattern.test(line));
          const allNumbered = lines.every((line) => numberedPattern.test(line));

          return {
            isList: allBullets || allNumbered,
            isOrdered: allNumbered,
          };
        };

        // Helper to convert markdown list content to TipTap list nodes
        const parseListContent = (content: string, isOrdered: boolean): TiptapNode[] => {
          const lines = content.split("\n").filter((l) => l.trim());
          const listItems: TiptapNode[] = [];

          for (const line of lines) {
            // Remove bullet or number prefix
            const textContent = line.replace(/^(•|\d+\.)\s+/, "");

            listItems.push({
              type: "listItem",
              attrs: { id: `node-${uuidv4()}` },
              content: [
                {
                  type: "paragraph",
                  attrs: { id: `node-${uuidv4()}` },
                  content: parseMDContent(textContent),
                },
              ],
            });
          }

          return [
            {
              type: "list",
              attrs: {
                id: `node-${uuidv4()}`,
                listType: isOrdered ? "ordered" : "unordered",
              },
              content: listItems,
            },
          ];
        };

        // Check if content is a list
        const quoteContent = node.content ?? "";
        const listInfo = isListContent(quoteContent);

        // Build blockquote content
        let blockquoteContent: TiptapNode[];

        if (listInfo.isList) {
          // Content is a list - convert to list node
          blockquoteContent = parseListContent(quoteContent, listInfo.isOrdered);
        } else {
          // Regular text content
          // Determine the child node type based on text_style
          let childType = "paragraph";
          let level: number | undefined;

          if (node.text_style === "h1") {
            childType = "heading";
            level = 1;
          } else if (node.text_style === "h2") {
            childType = "heading";
            level = 2;
          } else if (node.text_style === "h3" || node.text_style === "subtext") {
            childType = "heading";
            level = 3;
          }

          const childAttrs: Record<string, unknown> = {
            textAlign: node.align || "left",
            id: `node-${uuidv4()}`,
          };

          if (level !== undefined) {
            childAttrs.level = level;
          }

          blockquoteContent = [
            {
              type: childType,
              attrs: childAttrs,
              content: quoteContent.trim() ? parseMDContent(quoteContent) : [],
            },
          ];
        }

        return [
          {
            type: "blockquote",
            attrs: {
              textAlign: node.align || "left",
              id: `node-${uuidv4()}`,
              ...(node.border_color && { borderColor: node.border_color }),
              ...readQuoteFrameAttrs(node),
              ...readTypographyAttrs(
                node,
                (node.text_style ?? "quote") as TextTier,
                true,
                documentFontSize
              ),
              ...(node.background_color && { backgroundColor: node.background_color }),
              ...(node.locales && { locales: node.locales }),
              ...(node.if !== undefined && { if: node.if }),
            },
            content: blockquoteContent,
          },
        ];
      }

      case "image": {
        // Migration logic for image width:
        // - If width is in pixels (e.g., "500px") and image_natural_width exists: convert to percentage
        // - If width is in pixels but no image_natural_width: default to 100%
        // - If width is in percentage (e.g., "50%"): use as-is but clamp to valid range
        // - UI expects percentage (1-100)
        const widthAttrs: { width?: number; imageNaturalWidth?: number } = {};

        if (node.width) {
          const isPixels = node.width.endsWith("px");
          const isPercentage = node.width.endsWith("%");
          const numericWidth = parseInt(node.width.replace(/%|px/, ""));

          if (isPixels) {
            if (node.image_natural_width && node.image_natural_width > 0) {
              // Convert pixels to percentage: percentage = (pixelWidth / naturalWidth) * 100
              const percentageWidth = Math.round((numericWidth / node.image_natural_width) * 100);
              widthAttrs.width = Math.max(1, Math.min(100, percentageWidth));
              widthAttrs.imageNaturalWidth = node.image_natural_width;
            } else {
              // Pixel value without natural width - default to 100% (full width)
              widthAttrs.width = 100;
            }
          } else if (isPercentage) {
            // Already in percentage format - clamp to valid range
            widthAttrs.width = Math.max(1, Math.min(100, numericWidth));
          } else {
            // Numeric value without unit - treat as percentage, clamp to valid range
            widthAttrs.width = Math.max(1, Math.min(100, numericWidth));
          }
        }

        // Support both flat properties (border_color, border_size) and legacy nested format (border.color, border.size)
        const imageBorderSize = node.border_size || node.border?.size;
        const imageBorderColor = node.border_color || node.border?.color;

        return [
          {
            type: "imageBlock",
            attrs: {
              // Treat blank image placeholder as empty to show the upload UI
              sourcePath: isBlankImageSrc(node.src) ? "" : node.src,
              id: `node-${uuidv4()}`,
              ...(node.href && { link: node.href }),
              ...(node.align && { alignment: node.align }),
              ...(node.alt_text && { alt: node.alt_text }),
              ...widthAttrs,
              ...(imageBorderSize && {
                borderWidth: parseInt(imageBorderSize),
                ...(imageBorderColor && { borderColor: imageBorderColor }),
              }),
              ...(node.locales && { locales: node.locales }),
              ...(node.if !== undefined && { if: node.if }),
            },
          },
        ];
      }

      case "divider": {
        // Support both border_width (current) and width (legacy) properties
        const dividerWidth = node.border_width || node.width;
        return [
          {
            type: "divider",
            attrs: {
              id: `node-${uuidv4()}`,
              ...(node.color && { color: node.color }),
              ...(dividerWidth && { size: parseInt(dividerWidth) }),
              ...(node.padding && { padding: parseInt(node.padding) }),
              variant: node.color === "transparent" ? "spacer" : "divider",
              ...(node.if !== undefined && { if: node.if }),
            },
          },
        ];
      }

      case "html":
        return [
          {
            type: "customCode",
            attrs: {
              id: `node-${uuidv4()}`,
              code: node.content || "<!-- Add your HTML code here -->",
              ...(node.locales && { locales: node.locales }),
              ...(node.if !== undefined && { if: node.if }),
            },
          },
        ];

      case "group": {
        // Group in Elemental maps to column in TipTap
        // Count the number of elements to determine columns
        const columnsCount = node.elements ? node.elements.length : 2;

        // Parse padding from "6px 0px" format
        let paddingVertical = 0;
        let paddingHorizontal = 0;
        if (node.padding) {
          const paddingParts = node.padding.split(" ");
          paddingVertical = parseInt(paddingParts[0], 10) || 0;
          paddingHorizontal = parseInt(paddingParts[1] || paddingParts[0], 10) || 0;
        }

        // Parse border properties
        let borderWidth = 0;
        let borderRadius = 0;
        let borderColor = "transparent";
        if (node.border) {
          if (node.border.size) {
            borderWidth = parseInt(node.border.size, 10) || 0;
          }
          if (node.border.radius !== undefined) {
            borderRadius = node.border.radius;
          }
          if (node.border.color) {
            borderColor = node.border.color;
          }
        }

        // Background color
        const backgroundColor = node.background_color || "transparent";

        // Generate a unique ID for this column
        const columnId = `node-${uuidv4()}`;

        // Calculate equal width for each cell
        const cellWidth = 100 / columnsCount;

        // Create cells from the elements in the group
        const cells = node.elements
          ? node.elements.map((element, index) => {
              // Check if this is a placeholder text element (empty cell marker)
              const isPlaceholder =
                element.type === "text" &&
                "content" in element &&
                element.content?.trim() === "Drag and drop content blocks" &&
                element.align === "left";

              // If it's a placeholder, create an empty cell
              if (isPlaceholder) {
                return {
                  type: "columnCell",
                  attrs: {
                    id: `cell-${uuidv4()}`,
                    index,
                    columnId,
                    isEditorMode: false,
                    width: cellWidth,
                  },
                  content: [],
                };
              }

              // Check if this is a nested group (used to represent multiple elements in one cell)
              let cellContent: TiptapNode[];
              if (element.type === "group" && element.elements) {
                // Unwrap nested group elements directly into the cell
                cellContent = element.elements.flatMap(convertNode) as TiptapNode[];
              } else {
                // Convert single element to TipTap nodes
                cellContent = convertNode(element) as TiptapNode[];
              }

              // If the element converts to nothing, use an empty cell
              const content =
                cellContent.length > 0
                  ? cellContent
                  : [
                      {
                        type: "paragraph",
                        attrs: {
                          textAlign: "left",
                          id: `node-${uuidv4()}`,
                        },
                        content: [],
                      },
                    ];

              return {
                type: "columnCell",
                attrs: {
                  id: `cell-${uuidv4()}`,
                  index,
                  columnId,
                  isEditorMode: cellContent.length > 0,
                  width: cellWidth,
                },
                content,
              };
            })
          : [
              // No elements - create empty cells (2 columns at 50% each)
              {
                type: "columnCell",
                attrs: {
                  id: `cell-${uuidv4()}`,
                  index: 0,
                  columnId,
                  isEditorMode: false,
                  width: 50,
                },
                content: [
                  {
                    type: "paragraph",
                    attrs: {
                      textAlign: "left",
                      id: `node-${uuidv4()}`,
                    },
                    content: [],
                  },
                ],
              },
              {
                type: "columnCell",
                attrs: {
                  id: `cell-${uuidv4()}`,
                  index: 1,
                  columnId,
                  isEditorMode: false,
                  width: 50,
                },
                content: [
                  {
                    type: "paragraph",
                    attrs: {
                      textAlign: "left",
                      id: `node-${uuidv4()}`,
                    },
                    content: [],
                  },
                ],
              },
            ];

        return [
          {
            type: "column",
            attrs: {
              id: columnId,
              columnsCount: Math.min(Math.max(columnsCount, 1), 4), // Clamp between 1-4
              paddingVertical,
              paddingHorizontal,
              backgroundColor,
              borderWidth,
              borderRadius,
              borderColor,
              ...(node.locales && { locales: node.locales }),
              ...(node.if !== undefined && { if: node.if }),
            },
            content: [
              {
                type: "columnRow",
                content: cells,
              },
            ],
          },
        ];
      }

      case "columns": {
        // Columns container in Elemental maps to column in TipTap
        // Each child "column" element becomes a columnCell
        const columnElements = node.elements || [];
        const columnsCount = columnElements.length || 2;

        // Generate a unique ID for this column
        const columnId = `node-${uuidv4()}`;

        // Create cells from the column elements
        const cells = columnElements.map((columnElement, index) => {
          // Each column element contains its own elements
          const columnContent = columnElement.elements || [];

          // Check if this is a placeholder (empty column)
          const isPlaceholder =
            columnContent.length === 1 &&
            columnContent[0].type === "text" &&
            "content" in columnContent[0] &&
            (columnContent[0] as { content?: string }).content?.trim() ===
              "Drag and drop content blocks";

          // Parse Frame attributes from individual column element
          let cellPaddingVertical = 6;
          let cellPaddingHorizontal = 6;
          if (columnElement.padding) {
            const paddingParts = columnElement.padding.replace(/px/g, "").split(" ");
            const vertical = parseInt(paddingParts[0], 10);
            const horizontal = parseInt(paddingParts[1] || paddingParts[0], 10);
            if (!isNaN(vertical)) cellPaddingVertical = vertical;
            if (!isNaN(horizontal)) cellPaddingHorizontal = horizontal;
          }

          const cellBackgroundColor = columnElement.background_color || "transparent";

          // Parse Border attributes from individual column element
          let cellBorderWidth = 0;
          let cellBorderRadius = 0;
          let cellBorderColor = "transparent";

          if (columnElement.border_width) {
            const parsed = parseInt(columnElement.border_width, 10);
            if (!isNaN(parsed)) cellBorderWidth = parsed;
          }

          if (columnElement.border_radius) {
            const parsed = parseInt(columnElement.border_radius, 10);
            if (!isNaN(parsed)) cellBorderRadius = parsed;
          }

          if (columnElement.border_color) {
            cellBorderColor = columnElement.border_color;
          }

          // Parse width from column element (e.g., "25%" -> 25)
          // Default to equal distribution based on columns count
          let cellWidth = 100 / columnsCount;
          if (columnElement.width) {
            const parsed = parseFloat(columnElement.width.replace("%", ""));
            if (!isNaN(parsed)) cellWidth = parsed;
          }

          if (isPlaceholder) {
            return {
              type: "columnCell",
              attrs: {
                id: `cell-${uuidv4()}`,
                index,
                columnId,
                isEditorMode: false,
                width: cellWidth,
                paddingHorizontal: cellPaddingHorizontal,
                paddingVertical: cellPaddingVertical,
                backgroundColor: cellBackgroundColor,
                borderWidth: cellBorderWidth,
                borderRadius: cellBorderRadius,
                borderColor: cellBorderColor,
              },
              content: [],
            };
          }

          // Convert column content to TipTap nodes
          const cellContent = columnContent.flatMap(convertNode) as TiptapNode[];

          // If the content converts to nothing, use an empty paragraph
          const content =
            cellContent.length > 0
              ? cellContent
              : [
                  {
                    type: "paragraph",
                    attrs: {
                      textAlign: "left",
                      id: `node-${uuidv4()}`,
                    },
                    content: [],
                  },
                ];

          return {
            type: "columnCell",
            attrs: {
              id: `cell-${uuidv4()}`,
              index,
              columnId,
              isEditorMode: cellContent.length > 0,
              width: cellWidth,
              paddingHorizontal: cellPaddingHorizontal,
              paddingVertical: cellPaddingVertical,
              backgroundColor: cellBackgroundColor,
              borderWidth: cellBorderWidth,
              borderRadius: cellBorderRadius,
              borderColor: cellBorderColor,
            },
            content,
          };
        });

        // If no column elements, create default empty cells (2 columns at 50% each)
        const finalCells =
          cells.length > 0
            ? cells
            : [
                {
                  type: "columnCell",
                  attrs: {
                    id: `cell-${uuidv4()}`,
                    index: 0,
                    columnId,
                    isEditorMode: false,
                    width: 50,
                  },
                  content: [
                    {
                      type: "paragraph",
                      attrs: {
                        textAlign: "left",
                        id: `node-${uuidv4()}`,
                      },
                      content: [],
                    },
                  ],
                },
                {
                  type: "columnCell",
                  attrs: {
                    id: `cell-${uuidv4()}`,
                    index: 1,
                    columnId,
                    isEditorMode: false,
                    width: 50,
                  },
                  content: [
                    {
                      type: "paragraph",
                      attrs: {
                        textAlign: "left",
                        id: `node-${uuidv4()}`,
                      },
                      content: [],
                    },
                  ],
                },
              ];

        // Parse Frame attributes from Elemental format
        let paddingVertical = 0;
        let paddingHorizontal = 0;
        if (node.padding) {
          const paddingParts = node.padding.replace(/px/g, "").split(" ");
          const vertical = parseInt(paddingParts[0], 10);
          const horizontal = parseInt(paddingParts[1] || paddingParts[0], 10);
          if (!isNaN(vertical)) paddingVertical = vertical;
          if (!isNaN(horizontal)) paddingHorizontal = horizontal;
        }

        const backgroundColor = node.background_color || "transparent";

        // Parse Border attributes from Elemental format
        let borderWidth = 0;
        let borderRadius = 0;
        let borderColor = "transparent";

        if (node.border_width) {
          const parsed = parseInt(node.border_width, 10);
          if (!isNaN(parsed)) borderWidth = parsed;
        }

        if (node.border_radius) {
          const parsed = parseInt(node.border_radius, 10);
          if (!isNaN(parsed)) borderRadius = parsed;
        }

        if (node.border_color) {
          borderColor = node.border_color;
        }

        return [
          {
            type: "column",
            attrs: {
              id: columnId,
              columnsCount: Math.min(Math.max(columnsCount, 1), 4), // Clamp between 1-4
              paddingVertical,
              paddingHorizontal,
              backgroundColor,
              borderWidth,
              borderRadius,
              borderColor,
              ...(node.locales && { locales: node.locales }),
              ...(node.if !== undefined && { if: node.if }),
            },
            content: [
              {
                type: "columnRow",
                content: finalCells,
              },
            ],
          },
        ];
      }

      case "column": {
        // Individual column elements should not appear at the top level
        // They should be nested within a "columns" container
        // If we encounter one at the top level, treat its elements as block content
        const columnContent = node.elements || [];
        return columnContent.flatMap(convertNode) as TiptapNode[];
      }

      case "list": {
        // Convert Elemental list to TipTap list
        const listItems =
          node.elements?.map(
            (listItem: {
              type: string;
              elements?: unknown[];
              content?: string;
              locales?: Record<string, unknown>;
              background_color?: string;
            }) => {
              let content: TiptapNode[] = [];

              if (listItem.elements && Array.isArray(listItem.elements)) {
                // Group consecutive inline elements (string, link, img) into
                // a single paragraph. Only start a new paragraph when a nested
                // list is encountered.
                let pendingInline: ElementalTextContentNode[] = [];

                const flushInline = () => {
                  if (pendingInline.length > 0) {
                    content.push({
                      type: "paragraph",
                      attrs: { textAlign: "left", id: `node-${uuidv4()}` },
                      content: convertElementsArrayToTiptapNodes(pendingInline),
                    });
                    pendingInline = [];
                  }
                };

                for (const subElement of listItem.elements) {
                  const el = subElement as { type: string };
                  if (
                    el.type === "string" ||
                    el.type === "text" ||
                    el.type === "link" ||
                    el.type === "img"
                  ) {
                    pendingInline.push(el as unknown as ElementalTextContentNode);
                  } else if (el.type === "list") {
                    flushInline();
                    content.push(...convertNode(el as unknown as ElementalNode));
                  }
                }

                flushInline();
              } else if ("content" in listItem && typeof listItem.content === "string") {
                // Simple list item with content string (legacy format)
                content = [
                  {
                    type: "paragraph",
                    attrs: { textAlign: "left", id: `node-${uuidv4()}` },
                    content: parseMDContent(listItem.content),
                  },
                ];
              }

              // If no content, add empty paragraph
              if (content.length === 0) {
                content = [
                  {
                    type: "paragraph",
                    attrs: { textAlign: "left", id: `node-${uuidv4()}` },
                    content: [],
                  },
                ];
              }

              return {
                type: "listItem",
                attrs: {
                  id: `node-${uuidv4()}`,
                  ...(listItem.locales && { locales: listItem.locales }),
                  ...(listItem.background_color &&
                    listItem.background_color !== "transparent" && {
                      backgroundColor: listItem.background_color,
                    }),
                },
                content,
              };
            }
          ) || [];

        // Parse padding string (e.g., "10px" or "10px 20px") into vertical/horizontal values
        let paddingVertical = 0;
        let paddingHorizontal = 0;
        if (node.padding) {
          const paddingParts = node.padding.split(/\s+/).map((p: string) => parseInt(p, 10) || 0);
          if (paddingParts.length === 1) {
            paddingVertical = paddingParts[0];
            paddingHorizontal = paddingParts[0];
          } else if (paddingParts.length >= 2) {
            paddingVertical = paddingParts[0];
            paddingHorizontal = paddingParts[1];
          }
        }

        return [
          {
            type: "list",
            attrs: {
              id: `node-${uuidv4()}`,
              listType: node.list_type || "unordered",
              ...(paddingVertical > 0 && { paddingVertical }),
              ...(paddingHorizontal > 0 && { paddingHorizontal }),
              ...readTypographyAttrs(node, "text", false, documentFontSize),
              ...(node.loop && { loop: node.loop }),
              ...(node.if !== undefined && { if: node.if }),
            },
            content:
              listItems.length > 0
                ? listItems
                : [
                    {
                      type: "listItem",
                      attrs: { id: `node-${uuidv4()}` },
                      content: [
                        {
                          type: "paragraph",
                          attrs: { textAlign: "left", id: `node-${uuidv4()}` },
                          content: [],
                        },
                      ],
                    },
                  ],
          },
        ];
      }

      default:
        return [];
    }
  };

  // Process elements to convert consecutive action nodes to ButtonRow (only for Inbox channel)
  const processedElements: TiptapNode[] = [];
  const convertedNodes = targetChannelElements.flatMap(convertNode);

  for (let i = 0; i < convertedNodes.length; i++) {
    const currentNode = convertedNodes[i];
    const nextNode = convertedNodes[i + 1];

    // Two adjacent Inbox actions render as a row. The node type is already Inbox-specific, so
    // the channel check is belt and braces rather than the thing doing the work.
    if (
      currentNode.type === "inboxAction" &&
      nextNode?.type === "inboxAction" &&
      specifiedChannelName === "inbox"
    ) {
      // Convert to ButtonRow, preserving the original button styles from the sidebar settings
      const buttonRowNode: TiptapNode = {
        type: "buttonRow",
        attrs: {
          id: `node-${uuidv4()}`,
          button1Label: currentNode.attrs?.label || "Button 1",
          button1Link: currentNode.attrs?.link || "",
          // No colors: an Inbox action node has none to carry.
          button1ActionStyle: currentNode.attrs?.actionStyle ?? "button",
          ...(currentNode.attrs?.if !== undefined ? { button1If: currentNode.attrs.if } : {}),
          ...(currentNode.attrs?.locales ? { button1Locales: currentNode.attrs.locales } : {}),
          button2Label: nextNode.attrs?.label || "Button 2",
          button2Link: nextNode.attrs?.link || "",
          button2ActionStyle: nextNode.attrs?.actionStyle ?? "secondary",
          ...(nextNode.attrs?.if !== undefined ? { button2If: nextNode.attrs.if } : {}),
          ...(nextNode.attrs?.locales ? { button2Locales: nextNode.attrs.locales } : {}),
        },
      };

      processedElements.push(buttonRowNode);
      i++; // Skip the next node since we've processed it
    } else {
      processedElements.push(currentNode);
    }
  }

  return {
    type: "doc",
    content: processedElements,
  };
}
